import * as Sentry from '@sentry/react';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
} from 'axios';
import Router from 'next/router';

import { isDevelopment } from 'src/utils';

const DEFAULT_TIMEOUT = 30_000;
const DEFAULT_HEADERS = {
  'Content-type': 'application/json',
};

const handleUnauthorizedError = (error: AxiosError): void => {
  if (error.response?.status === 401) {
    if (typeof window !== 'undefined') {
      Router.push('/').then((r) => r);
    } else {
      console.error('Error: ', error);
    }
  }
};

const captureErrorInSentry = (error: AxiosError): void => {
  const status = error.response?.status;

  Sentry.withScope((scope) => {
    scope.setExtras({
      url: error.config?.url,
      method: error.config?.method,
      headers: error.config?.headers,
      body: error.config?.data,
      status,
      response: error.response?.data,
    });
    Sentry.captureException(error);
  });
};

const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout: DEFAULT_TIMEOUT,
    headers: DEFAULT_HEADERS,
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      captureErrorInSentry(error);
      handleUnauthorizedError(error);
      return Promise.reject(error);
    },
  );

  return instance;
};

const getAnalyticsBaseUrl = (): string => {
  return isDevelopment
    ? 'https://dev.analytics.mentalgrowth.app/api/v1/'
    : process.env.NEXT_PUBLIC_API_ANALYTICS_ENDPOINT_V2 || '';
};

const axiosAnalytics = createAxiosInstance(getAnalyticsBaseUrl());
const axiosMicroservice = createAxiosInstance(
  process.env.NEXT_PUBLIC_API_PAYMENT_ENDPOINT || '',
);
const axiosSuperYouth = createAxiosInstance(
  isDevelopment
    ? 'https://dev.superyouth.mentalgrowth.app'
    : 'https://superyouth.mentalgrowth.app',
);
const axiosAnalyticsV2 = createAxiosInstance(
  process.env.NEXT_PUBLIC_API_ANALYTICS_ENDPOINT_V2 || '',
);

export { axiosMicroservice, axiosAnalytics, axiosAnalyticsV2, axiosSuperYouth };
